import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import styles from "./UnderwritingSections.module.scss";
import UnderWritingFormProvider, {
  UnderwritingFormContext,
} from "../context/UnderwritingFormContext";
import MeritoButton from "../../../components/common/MeritoButtons/MeritoButtons";
import {
  HiChevronLeft as LeftChevron,
  HiChevronRight as RightChevron,
  HiPencil as PencilIcon,
} from "react-icons/hi";
import { styled } from "@mui/system";
import Stepper from "@mui/material/Stepper";
import {
  Step,
  StepButton,
  StepConnector,
  StepIconProps,
  StepLabel,
  stepConnectorClasses,
} from "@mui/material";
import {
  BankStatementWarningType,
  LendingStatus,
  StatementAnalysisErrorTypes,
  StatementAnalysisStatus,
  StatementStepType,
  UnderWritingStepsTitle,
  UnderwritingFormStatus,
  UnderwritingSteps,
  underwritingRoutes,
  uploadStates,
} from "../../../interfaces/interfaces";
import {
  Route,
  Routes,
  useBlocker,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router";
import ConnectAccounts from "../ConnectAccounts/ConnectAccounts";
import ConfirmDialog from "../../../components/common/ConfirmDialog";
import { FaCircleExclamation as Exclamation } from "react-icons/fa6";
import CompanyDetails from "../CompanyDetails/CompanyDetails";
import KeyEvents from "../KeyEvents/KeyEvents";
import Investments from "../Investments/Investments";
import RevenueDetails from "../Revenue Details/RevenueDetails";
import UploadDocuments from "../UploadDocuments/UploadDocuments";
import UpdatePnl from "../UpdatePnl/UpladePnl";
import { RestS } from "../../../rest/rest-service";
import { HiCheck as CheckIcon } from "react-icons/hi";
import HeaderTitle from "../../../components/common/HeaderTitle";
import useSocketOnPc from "../../../components/Hooks/useSocketPc";
import useSocketOnUnderwriting from "../../../components/Hooks/useSocketUnderwriting";
import usePageTitle from "../../../components/Hooks/usePageTitle";
import UnderwritingDeclaration from "../UnderwritingDeclaration/UnderwritingDeclaration";
import DirectorDetails from "../Director/Director";
import ShareholdersRevamp from "../CompanyDetails/ShareholdersRevamp";
import { getIn } from "final-form";
import InvestmentsRevamp from "../Investments/InvestmentRevamp";
import ChecklistPage from "../Checklist/Checklist";
import { useAutoSave, clearLocalStorageDraft } from "../../../components/Hooks/useAutoSave";

const UnderwritingStepper = styled(Stepper)({
  // Target the alternative label style for all StepLabel components within this Stepper
  ".MuiStepLabel-labelContainer .MuiStepLabel-label": {
    marginTop: "0.5rem !important", // Remove the margin-top
  },
});

const UnderwritingConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.active}`]: {
    "& .MuiStepConnector-line": {
      borderColor: "#100828",
      borderWidth: "2px",
    },
  },
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 14,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  [`&.${stepConnectorClasses.root}`]: {
    "& .MuiStepConnector-line": {
      borderColor: "#d4d4d4",
      borderWidth: "2px",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    "& .MuiStepConnector-line": {
      borderColor: "#d4d4d4",
      borderWidth: "2px",
    },
  },
}));

const UnderwritingStepIcon = (props) => {
  const { active, completed, icon, disabled } = props;

  // console.log('icon', icon, disabled, active)

  return (
    <div
      className={`${styles.customStepIcon} ${disabled && !active
        ? styles.disabled
        : completed
          ? styles.completed
          : active
            ? styles.active
            : ""
        } ${active && completed ? styles.completedActive : ""}`}
    >
      {active ? (
        <div>
          <PencilIcon />
        </div>
      ) : completed ? (
        <div>
          <CheckIcon />
        </div>
      ) : (
        <div
          className={`${styles.numberIcon} ${active || completed ? styles.active : ""
            }`}
        >
          {icon}
        </div>
      )}
    </div>
  );
};

export const initialStepperlist = [
  {
    name: UnderwritingSteps.accounts,
    title: UnderWritingStepsTitle.accounts,
    link: underwritingRoutes.underwritingAccounts,
  },
  {
    name: UnderwritingSteps.documents,
    title: UnderWritingStepsTitle.documents,
    link: underwritingRoutes.underwritingDocuments,
  },
  {
    name: UnderwritingSteps.company,
    title: UnderWritingStepsTitle.company,
    link: underwritingRoutes.underwritingCompany,
  },
  // {
  //     name: UnderwritingSteps.director,
  //     title: UnderWritingStepsTitle.director,
  //     link: underwritingRoutes.underwritingDirector
  // },
  {
    name: UnderwritingSteps.revenue,
    title: UnderWritingStepsTitle.revenue,
    link: underwritingRoutes.underwritingRevenue,
  },

  // {
  //     name: UnderwritingSteps.pnl,
  //     title: UnderWritingStepsTitle.pnl,
  //     link: underwritingRoutes.underwritingPnl
  // },
  {
    name: UnderwritingSteps.investments,
    title: UnderWritingStepsTitle.investments,
    link: underwritingRoutes.underwritingInvestments,
  },
];

export const getNextStepperLink = (currentStep: UnderwritingSteps) => {
  const currentStepIndex = initialStepperlist.findIndex(
    (item) => item.name == currentStep
  );
  if (currentStepIndex == initialStepperlist.length - 1) return null;
  return initialStepperlist[currentStepIndex + 1]?.link;
};

export const linkObj = {
  [underwritingRoutes.underwritingAccounts]: UnderwritingSteps.accounts,
  [underwritingRoutes.underwritingCompany]: UnderwritingSteps.company,
  [underwritingRoutes.underwritingRevenue]: UnderwritingSteps.revenue,
  // [underwritingRoutes.underwritingKeyEvents]: UnderwritingSteps.keyEvents,
  [underwritingRoutes.underwritingDirector]: UnderwritingSteps.director,
  [underwritingRoutes.underwritingInvestments]: UnderwritingSteps.investments,
  [underwritingRoutes.underwritingDocuments]: UnderwritingSteps.documents,
  // [underwritingRoutes.underwritingPnl]: UnderwritingSteps.pnl
};

const UnderwritingSections = () => {
  const [stepperList, setStepperList] = useState(initialStepperlist);
  const [activeStep, setActiveStep] = useState(0);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    formData,
    setFormData,
    formRefs,
    formStepsValidity,
    formCompleteStatus,
    handleAndSetFormStatuses,
    uploadingPendingItems,
    statementAnalysisJobs,
    underwritingFileJobs,
    fetchUnderwritingFileJobs,
    extraFormDetails,
  } = useContext(UnderwritingFormContext);
  const [showConnectAccountsWarning, setShowConnectAccountsWarning] =
    useState(false);
  const [showBankStatementWarning, setShowBankStatementWarning] = useState({
    state: false,
    warningType: null,
    deleteDocIds: [],
  });

  const [submitLoading, setSubmitLoading] = useState(false);

  const { setHeaderContent, resetHeaderContent } = useOutletContext<any>();
  const [bankStatementStepValues, setBankStatementValues] = useState(null);
  const [formPristine, setFormPristine] = useState(false);
  const [showSaveWarning, setShowSaveWarning] = useState({
    state: false,
    newDocIds: null,
  });
  const [navigateToState, setNavigateToState] = useState({
    stepIndex: null,
    link: "",
  });
  const [accountPopupNavigationState, setAccountPopupNavigationState] =
    useState({
      stepIndex: null,
    });

  const socketUw = useSocketOnUnderwriting();
  const pageTitle = usePageTitle("Merito Investment | Merito");

  const currentStepName = stepperList[activeStep]?.name ?? "";
  const { saveToLocalStorage, flushToLocalStorage, saveToDB, getLatestValues } = useAutoSave({
    companyId: extraFormDetails?.companyId?.toString() ?? "",
    currentStep: currentStepName,
  });

  // Refs to avoid stale closures in event handlers (beforeunload, visibilitychange)
  const formDataRef = useRef(formData);
  const formPristineRef = useRef(formPristine);
  const currentStepNameRef = useRef(currentStepName);
  // Flag to let useBlocker skip internal navigations (stepper, next, back button in UI)
  const isInternalNavigationRef = useRef(false);

  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { formPristineRef.current = formPristine; }, [formPristine]);
  useEffect(() => { currentStepNameRef.current = currentStepName; }, [currentStepName]);

  useEffect(() => {
    setHeaderContent(
      <HeaderTitle isNav={false} headerTitle={`Merito Investment`} />
    );
    return () => {
      resetHeaderContent();
      pageTitle("");
    };
  }, []);

  // Task 4 + Subtask 3.2: Native browser warning + save to DB on tab/browser close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!formPristineRef.current) {
        e.preventDefault();
        e.returnValue = "";

        // Best-effort save: use latest FormSpy values, fall back to context
        const latest = getLatestValues();
        const valuesToSave = Object.keys(latest).length > 0 ? latest : formDataRef.current;
        flushToLocalStorage(valuesToSave);
        saveToDB(valuesToSave, currentStepNameRef.current, { keepalive: true });
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [saveToDB, flushToLocalStorage, getLatestValues]);

  // Subtask 3.3: Save to DB when user switches to another tab
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden" && !formPristineRef.current) {
        const latest = getLatestValues();
        const valuesToSave = Object.keys(latest).length > 0 ? latest : formDataRef.current;
        saveToDB(valuesToSave, currentStepNameRef.current, {
          keepalive: false,
        });
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [saveToDB, getLatestValues]);

  // Task 4: Intercept sidebar/header/back-button navigation to auto-save before leaving
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => {
      if (isInternalNavigationRef.current) {
        isInternalNavigationRef.current = false;
        return false;
      }
      return !formPristineRef.current && currentLocation.pathname !== nextLocation.pathname;
    },
  );

  // When blocker fires for external navigation, auto-save and proceed (no modal)
  useEffect(() => {
    if (blocker.state === "blocked") {
      const latest = getLatestValues();
      const valuesToSave = Object.keys(latest).length > 0 ? latest : formDataRef.current;
      flushToLocalStorage(valuesToSave);
      saveToDB(valuesToSave, currentStepNameRef.current, { keepalive: false });
      blocker.proceed?.();
    }
  }, [blocker.state]);

  const formSectionRefs = useRef({
    [UnderwritingSteps.company]: React.createRef(),
    [UnderwritingSteps.revenue]: React.createRef(),
    // [UnderwritingSteps.keyEvents]: React.createRef(),
    [UnderwritingSteps.documents]: React.createRef(),
    [UnderwritingSteps.director]: React.createRef(),
    // [UnderwritingSteps.pnl]: React.createRef(),

    [UnderwritingSteps.investments]: React.createRef(),
  });

  const [isDeclarationOpen, setIsDeclarationOpen] = useState(false);
  const [finalFormData, setFinalFormData] = useState(null);

  const [submitWarning, setSubmitWarning] = useState({
    state: false,
    formValues: null,
  });

  const isChecklistPage =
    pathname == underwritingRoutes.underwritingChecklist ? true : false;

  useEffect(() => {
    // if (update?.updateAvailable) {
    const bodyElement = document.getElementsByTagName("body")[0];
    bodyElement.style.overflowY = "hidden";
    const pageIndexLayout: any = document.getElementById("page_body");
    if (pageIndexLayout) {
      pageIndexLayout.style.overflowY = "hidden";
    }
    // console.log('pathname', pathname, formCompleteStatus);
    if (formCompleteStatus[linkObj[pathname]] != undefined) {
      const currentStep = linkObj[pathname];
      const currentStatus = formCompleteStatus[linkObj[pathname]];
      // if (currentStatus == UnderwritingFormStatus.INCOMPLETE) {
      //     const steps = Object.keys(formCompleteStatus);
      //     const currentStepIndex = steps.indexOf(currentStep);
      //     if (currentStepIndex == 0) return;
      //     const previousStep = steps[currentStepIndex - 1];
      //     console.log('previousStep', previousStep, formCompleteStatus);
      //     let index = 0;
      //     for (const stepItem of stepperList) {
      //         if (formCompleteStatus[stepItem.name] == UnderwritingFormStatus.INCOMPLETE) {
      //             setActiveStep(index);
      //             navigate(stepItem.link);
      //             break;
      //         }
      //         index++;
      //     }
      // }
    }

    return () => {
      // console.log('running this')
      const bodyElement = document.getElementsByTagName("body")[0];
      const pageIndexLayout: any = document.getElementById("page_body");
      if (pageIndexLayout) {
        pageIndexLayout.style.overflowY = "scroll";
        pageIndexLayout.style.padding = "1rem 1.5rem 2rem 1.5rem";
      }
    };
    // }
  }, []);

  const stepperLocationObj = useMemo(() => {
    return stepperList.reduce((prev, curVal, index) => {
      prev[curVal.link] = {
        ...curVal,
        stepIndex: index,
      };
      return prev;
    }, {});
  }, [stepperList]);

  useEffect(() => {
    let activeStepIndex = stepperLocationObj[pathname.toLowerCase()];
    if (activeStepIndex != undefined) {
      setActiveStep(activeStepIndex?.stepIndex);
    } else {
      setActiveStep(0);
    }
  }, [pathname]);

  const handleBankStatementWarning = (formValues: any, deleteDocIds: []) => {
    // console.log('bankStatementValues', bankStatementValues);
    // console.log('statementAnalysisJobs', statementAnalysisJobs);
    const { values } = formValues;
    const bankStatementData = values?.bankStatements;
    let otherError = false;
    let warningShown = false;
    const allBankStatementUploadedJobs = getIn(
      underwritingFileJobs,
      `${UnderwritingSteps.documents}`
    );
    let accountIndex = 0;
    for (const statementData of bankStatementData) {
      if (statementData?.statementType == StatementStepType.pdf_upload) {
        let statementIndex = 0;
        let isErrorShown = false;
        for (const statements of statementData?.statements) {
          const uploadedFile =
            allBankStatementUploadedJobs[
            `bankStatements[${accountIndex}].statements[${statementIndex}]`
            ];
          if (uploadedFile) {
            const requriePassword =
              uploadedFile?.status == uploadStates.requiredPassword;
            if (requriePassword) {
              setSubmitWarning({
                state: true,
                formValues: formValues,
                // newDocIds: bankStatementValues?.newDocIds
              });
              isErrorShown = true;
              otherError = true;
              break;
            }
          }
          statementIndex++;
        }
        if (isErrorShown) {
          otherError = true;
          warningShown = true;
          break;
        }
      }
      const currentJob = getIn(
        statementAnalysisJobs,
        `${statementData?.statementJobId}.${statementData?.statementType}`
      );
      // console.log('currentJob', currentJob);
      if (currentJob && currentJob?.status == StatementAnalysisStatus.failure) {
        const errorObj = currentJob?.errorDetails;
        const errorArr = errorObj?.errors?.length
          ? new Set(errorObj?.errors)
          : new Set();
        if (errorArr.has(StatementAnalysisErrorTypes.EOD_MISSING_RANGES)) {
          setShowBankStatementWarning({
            state: true,
            warningType: BankStatementWarningType.missingTransactions,
            deleteDocIds,
          });
          warningShown = true;
          otherError = true;
          break;
        }
      }
      // }
    }
    console.log("otherError", otherError);
    // if (!otherError) {
    //     setShowBankStatementWarning({
    //         state: true,
    //         warningType: BankStatementWarningType.uploadedAllStatement,
    //         deleteDocIds,
    //     })
    //     warningShown = true
    //     return;
    // }
    accountIndex++;
    return warningShown;
  };

  const handleNextStep = async () => {
    // console.log('activeStep', activeStep)
    if (activeStep == 0) {
      // if (formData['accountConnectConfirmation'] == undefined || !formData['accountConnectConfirmation']) {
      setShowConnectAccountsWarning(true);
      // } else {
      //     submitFormData();
      // }
    } else {
      const isLastStep = stepperList.length - 1 == activeStep;
      const currentStep = activeStep;
      const currentStepObj = stepperList[currentStep];
      const nextStep = isLastStep ? null : stepperList[currentStep + 1];
      const currentFormValues = await formSectionRefs?.current[
        currentStepObj?.name
      ].current.submitForm();
      // console.log('currentFormValues', currentFormValues);
      console.log("currentFormValues", currentFormValues);
      console.log("cuurentStepObj", currentStepObj);
      if (!currentFormValues?.success) {
        // console.log('invalid values', currentFormValues);
        setBankStatementValues(currentFormValues);
        if (currentStepObj?.name == UnderwritingSteps.documents) {
          setBankStatementValues(currentFormValues);
          const isWarningshow = handleBankStatementWarning(
            currentFormValues,
            currentFormValues?.deleteDocIds
          );

          if (isWarningshow) return {};
        }
        setSubmitWarning({
          state: true,
          formValues: currentFormValues,
        });
        return {};
      } else {
        // console.log('currentFormValues', currentFormValues);
        // return {}
        if (currentStepObj.name == UnderwritingSteps.documents) {
          // console.log("valid values", currentFormValues);
          setBankStatementValues(currentFormValues);
          // setShowBankStatementWarning(true);
          const isWarningShow = handleBankStatementWarning(
            currentFormValues,
            currentFormValues?.deleteDocIds
          );
          console.log("isWarningShow", isWarningShow);

          console.log("currentFormValues", currentFormValues);
          // if (!isWarningShow) {
          //     setSubmitWarning({
          //         state: true,
          //         formValues: currentFormValues
          //     })
          // }
          // return {};
          if (isWarningShow) return {};
        }
        if (!isLastStep) {
          setActiveStep(activeStep + 1);
          isInternalNavigationRef.current = true;
          navigate(nextStep?.link);
        }
        if (currentStepObj.name == UnderwritingSteps.keyEvents) {
          // console.log('currentFormValues', currentFormValues);
          let brandFormData = {
            brandEvents: Object.values(currentFormValues?.values?.keyEvents),
          };
          // console.log('brandFormData', brandFormData)
          if (brandFormData?.brandEvents && brandFormData?.brandEvents.length) {
            RestS.submitBrandEvents(brandFormData).catch((error) => {
              console.log("error", error);
            });
          }
          let submitFormData = {
            data: {
              ...formData,
            },
            step: currentStepObj.name,
          };
          onSubmitForm(submitFormData);
        } else {
          let submitFormData = {
            data: {
              // ...formData,
              ...currentFormValues?.values,
            },
            step: currentStepObj.name,
            // applicationStatus: isLastStep ? LendingStatus.PENDING_APPROVAL : LendingStatus.IN_PROGRESS,
            applicationStatus: LendingStatus.IN_PROGRESS,
            deleteDocIds: currentFormValues?.deleteDocIds ?? [],
          };
          // console.log('submitFormData', submitFormData)
          // return;
          if (
            !currentFormValues?.pristine ||
            formCompleteStatus[currentStepObj.name] ==
            UnderwritingFormStatus.INCOMPLETE ||
            isLastStep
          ) {
            // if (isLastStep) {
            //     setFinalFormData(submitFormData);
            //     setIsDeclarationOpen(true);
            // } else {
            onSubmitForm(submitFormData, isLastStep);
            // }
          }
        }
      }
    }
  };

  const submitFormData = () => {
    const currentStep = activeStep;
    const currentStepObj = stepperList[currentStep];
    const nextStep = stepperList[currentStep + 1];
    // console.log('currentStep', currentStep, 'activeStep', activeStep);
    if (currentStep == 0) {
      let submitFormData = {
        data: {
          ...formData,
          accountConnectConfirmation: true,
        },
        step: currentStepObj.name,
      };
      if (
        formData["accountConnectConfirmation"] == undefined ||
        !formData["accountConnectConfirmation"]
      ) {
        setShowConnectAccountsWarning(false);
        // setFormData({ ...formData, accountConnectConfirmation: true })
        setActiveStep(activeStep + 1);
        isInternalNavigationRef.current = true;
        navigate(nextStep?.link);
        onSubmitForm(submitFormData);
      } else {
        // setActiveStep(activeStep + 1);
        setShowConnectAccountsWarning(false);
        isInternalNavigationRef.current = true;
        navigate(nextStep?.link);
        onSubmitForm(submitFormData);
      }
    }
  };

  const submitAfterSaveWarning = (formValues: any) => {
    try {
      const currentStep = activeStep;
      const isLastStep = stepperList.length - 1 == activeStep;
      const currentStepObj = stepperList[currentStep];
      const nextStep = stepperList[currentStep + 1];
      let submitFormData = {
        data: {
          // ...formData,
          ...formValues?.values,
        },
        step: currentStepObj.name,
        deleteDocIds: formValues?.deleteDocIds,
        isIncomplete: true,
      };
      setActiveStep(activeStep + 1);
      isInternalNavigationRef.current = true;
      navigate(nextStep?.link);
      if (
        !formValues?.pristine ||
        formCompleteStatus[currentStepObj.name] ==
        UnderwritingFormStatus.INCOMPLETE
      ) {
        onSubmitForm(submitFormData, isLastStep);
      }
      // onSubmitForm(submitFormData);
      setSubmitWarning({
        state: false,
        formValues: null,
      });
    } catch (error) {
      console.log("error");
    }
  };

  const submitBankStatementData = () => {
    const currentStep = activeStep;
    const currentStepObj = stepperList[currentStep];
    const nextStep = stepperList[currentStep + 1];
    const currentBankStatementValues = { ...bankStatementStepValues };
    try {
      const overrideStatementError =
        showBankStatementWarning?.warningType ==
        BankStatementWarningType.missingTransactions;

      // console.log('overrideStatementError', overrideStatementError);
      // console.log('showBankStatementWarning', showBankStatementWarning);
      // return;
      let submitFormData = {
        data: {
          ...formData,
          ...currentBankStatementValues?.values,
        },
        step: currentStepObj.name,
        deleteDocIds: showBankStatementWarning?.deleteDocIds,
        overrideStatementError: overrideStatementError,
      };
      if (
        !currentBankStatementValues?.pristine || overrideStatementError
        // ||
        // formCompleteStatus[currentStepObj.name] ==
        // UnderwritingFormStatus.INCOMPLETE
      ) {
        onSubmitForm(submitFormData, false);
      }
      // else {
      isInternalNavigationRef.current = true;
      navigate(nextStep?.link);
      setActiveStep(currentStep + 1);
      // }
      setShowBankStatementWarning({
        state: false,
        warningType: null,
        deleteDocIds: [],
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  const delaySubmitting = async () => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        resolve(true);
      }, 5000);
    });
  };

  const onSubmitForm = async (submitFormData, isLastStep = false) => {
    try {
      if (isLastStep) setSubmitLoading(true);
      setFormData(submitFormData?.data);
      // await delaySubmitting()
      const data: any = await RestS.submitUnderwritingFormData(submitFormData);
      handleAndSetFormStatuses(data?.formStatuses);
      setFormData(data?.formData["data"]);
      clearLocalStorageDraft(extraFormDetails?.companyId?.toString() ?? "");
      if (isLastStep) {
        console.log("coming in last step");
        isInternalNavigationRef.current = true;
        navigate("/underwriting/application/checklist");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setSubmitLoading(false);
      setIsDeclarationOpen(false);
    }
  };

  // const getFormValidityState = useMemo(() => {
  //     const currentStep = stepperList[activeStep];
  //     if (activeStep == 0) return true;
  //     // console.log('formStepsValidityRef', formStepsValidity)

  //     return formStepsValidity[currentStep.name] ?? false;
  // }, [formStepsValidity, activeStep]);

  const checkSubmitBtnEnable = useMemo(() => {
    const currentStep = stepperList[activeStep];
    const stepsToCheck = new Set([
      UnderwritingSteps.company,
      UnderwritingSteps.revenue,
      UnderwritingSteps.documents,
    ]);
    console.log("uploadPendingItems", Array.from(uploadingPendingItems));
    if (stepsToCheck.has(currentStep?.name)) {
      return uploadingPendingItems?.length > 0 ? true : false;
    } else return false;
  }, [uploadingPendingItems, activeStep, formStepsValidity]);

  const allowSteps = useMemo(() => {
    const allSteps = Object.keys(formCompleteStatus);
    const allowedSteps = new Set();
    for (const step of stepperList) {
      if (formCompleteStatus[step.name] == UnderwritingFormStatus.COMPLETED) {
        allowedSteps.add(step.name);
      } else {
        allowedSteps.add(step.name);
        break;
      }
    }
    return allowedSteps;
  }, [formCompleteStatus]);

  const handleRandomNavigation = async (navigateToObj = null) => {
    if (isChecklistPage) {
      isInternalNavigationRef.current = true;
      navigate(stepperList[stepperList.length - 1].link);
      return;
    }
    let navigateTo = {
      stepIndex: activeStep == 0 ? null : activeStep - 1,
      link:
        activeStep == 0
          ? underwritingRoutes.underwritingHome
          : stepperList[activeStep - 1].link,
    };
    if (navigateToObj) {
      navigateTo = navigateToObj;
    }
    const isLastStep = stepperList.length - 1 == activeStep;
    if (activeStep == 0) {
      isInternalNavigationRef.current = true;
      navigate(navigateTo.link);
      if (navigateToObj) {
        setActiveStep(navigateTo.stepIndex);
      }
    } else {
      const currentStep = activeStep;
      const currentStepObj = stepperList[currentStep];
      const nextStep = isLastStep ? null : stepperList[currentStep + 1];
      const currentFormValues = await formSectionRefs?.current[
        currentStepObj?.name
      ].current.submitForm();
      // Auto-save on stepper click (Subtask 3.4)
      const valuesToSave = currentFormValues?.values ?? formData;
      if (!currentFormValues?.pristine) {
        // Update context so form has latest data when user navigates back
        setFormData(valuesToSave);
        // Flush to localStorage immediately (bypass debounce before unmount)
        flushToLocalStorage(valuesToSave);
        // Save to DB
        saveToDB(valuesToSave, currentStepObj.name, { keepalive: false });
      }
      setActiveStep(navigateTo.stepIndex);
      isInternalNavigationRef.current = true;
      navigate(navigateTo.link);
    }
  };

  const handlePnlSubmit = (submitValues) => {
    setFinalFormData(submitValues);
    setIsDeclarationOpen(true);
  };

  const handleFinalFormSubmit = (declarationValues) => {
    let submitFormData = {
      ...finalFormData,
      data: {
        ...finalFormData?.data,
        ...declarationValues,
      },
    };
    onSubmitForm(submitFormData, true);
  };

  const handleOnDiscardChanges = async (newDocIds) => {
    try {
      if (newDocIds && newDocIds.length) return;
      const response = await RestS.deleteUploadedJob({
        deleteDocIds: newDocIds,
      });
      fetchUnderwritingFileJobs();
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <>
      <div className={styles.underwritingSectionsRootContainer}>
        <div className={styles.actionBtnContainer}>
          <div className={styles.leftContainer}>
            <MeritoButton
              type="secondary-1"
              btnText={"Back"}
              icon={<LeftChevron />}
              onClick={() => {
                // if (activeStep == 0) {
                //     navigate(underwritingRoutes.underwritingHome)
                // } else {
                //     setActiveStep(activeStep - 1);
                //     navigate(stepperList[activeStep - 1].link)
                // }
                handleRandomNavigation();
              }}
            />
          </div>
          <div className={styles.rightContainer}>
            {isChecklistPage ? null : (
              <MeritoButton
                loading={submitLoading}
                // disabled={stepperList.length - 1 == activeStep ? !getFormValidityState : checkSubmitBtnEnable}
                disabled={checkSubmitBtnEnable}
                type="primary-1"
                btnText={"Next"}
                icon={<RightChevron />}
                isIconToRight={true}
                onClick={handleNextStep}
              />
            )}
          </div>
        </div>
        {isChecklistPage ? null : (
          <div className={styles.stepperRootContainer}>
            <UnderwritingStepper
              connector={<UnderwritingConnector />}
              alternativeLabel
              nonLinear
              activeStep={activeStep}
            >
              {stepperList.map((item, index) => {
                const isCompleted =
                  formCompleteStatus[item?.name] ==
                  UnderwritingFormStatus.COMPLETED;
                return (
                  <Step
                    completed={isCompleted}
                    // disabled={!isCompleted || !allowSteps.has(item.name)}
                    key={`${item.name}_${index}`}
                    onClick={(e) => {
                      if (activeStep == index) return;
                      // if ((isCompleted && activeStep != index) || allowSteps.has(item.name)) {
                      // navigate(item.link)
                      handleRandomNavigation({
                        stepIndex: index,
                        link: item.link,
                      });
                      // }
                    }}
                  >
                    <StepLabel
                      style={{ cursor: "pointer" }}
                      // style={{ cursor: isCompleted || activeStep == index || allowSteps.has(item.name) ? "pointer" : 'not-allowed' }}
                      StepIconComponent={(props) => (
                        <UnderwritingStepIcon
                          {...props}
                          disabled={!isCompleted}
                        />
                      )}
                    >
                      <div
                        className={`${styles.customStepLabel} ${activeStep == index ? styles.active : ""
                          }`}
                      >
                        {item.title}
                      </div>
                    </StepLabel>
                  </Step>
                );
              })}
            </UnderwritingStepper>
          </div>
        )}
        <Routes>
          <Route path={"/accounts"} element={<ConnectAccounts />} />
          <Route
            path={"/company-details"}
            element={
              <CompanyDetails
                ref={formSectionRefs?.current[UnderwritingSteps.company]}
                setFormPristine={setFormPristine}
                saveToLocalStorage={saveToLocalStorage}
              />
            }
          />
          {/* <Route path={'/director-details'} element={<DirectorDetails ref={formSectionRefs?.current[UnderwritingSteps.director]} setFormPristine={setFormPristine} />} /> */}
          <Route
            path={"/revenue-details"}
            element={
              <RevenueDetails
                ref={formSectionRefs?.current[UnderwritingSteps.revenue]}
                setFormPristine={setFormPristine}
                saveToLocalStorage={saveToLocalStorage}
              />
            }
          />
          <Route
            path={"/investments"}
            element={
              <InvestmentsRevamp
                ref={formSectionRefs?.current[UnderwritingSteps.investments]}
                setFormPristine={setFormPristine}
                saveToLocalStorage={saveToLocalStorage}
              />
            }
          />
          <Route
            path={"/upload-documents"}
            element={
              <UploadDocuments
                ref={formSectionRefs?.current[UnderwritingSteps.documents]}
                setFormPristine={setFormPristine}
                saveToLocalStorage={saveToLocalStorage}
              />
            }
          />
          <Route
            path={"/pnl"}
            element={
              <UpdatePnl
                ref={formSectionRefs?.current[UnderwritingSteps.pnl]}
                setFormPristine={setFormPristine}
                handlePnlSubmit={handlePnlSubmit}
              />
            }
          />

          <Route
            path={"/checklist"}
            element={<ChecklistPage stepperList={stepperList} />}
          />
        </Routes>
      </div>

      <ConfirmDialog
        modalStyles={{ maxWidth: "450px" }}
        showButton={false}
        visible={showConnectAccountsWarning}
        toggle={() => {
          setShowConnectAccountsWarning(!showConnectAccountsWarning);
        }}
        showCloseBtn={false}
        size={"md"}
      >
        <div className={styles.confirmationContainer}>
          <div className={styles.topContainer}>
            <div className={styles.iconContainer}>
              <Exclamation />
            </div>
            <div className={styles.title}>
              Are These Your Only Active Sales and Ad Channels?
              <br />
              <br />
            </div>
          </div>

          <div className={styles.bottomContainer}>
            <MeritoButton
              onClick={() => {
                setShowConnectAccountsWarning(false);
              }}
              size="lg"
              isRounded={true}
              btnType="button"
              type="secondary-2"
              btnText={"Connect More"}
            />
            <MeritoButton
              size="lg"
              onClick={() => {
                submitFormData();
              }}
              isRounded={true}
              btnType="button"
              type="primary-1"
              btnText="Confirm"
            />
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        modalStyles={{ maxWidth: "450px" }}
        showButton={false}
        visible={showSaveWarning?.state}
        toggle={() => {
          setShowSaveWarning({
            state: false,
            newDocIds: null,
          });
        }}
        showCloseBtn={false}
        size={"md"}
      >
        <div className={styles.confirmationContainer}>
          <div className={styles.topContainer}>
            <div className={styles.iconContainer}>
              <Exclamation />
            </div>
            <div className={styles.title}>
              Unsaved Changes Will Be Lost!
              <br />
              <br />
            </div>
          </div>

          <div className={styles.bottomContainer}>
            <MeritoButton
              onClick={() => {
                // setShowSaveWarning(false)
                // navigat
                if (navigateToState.stepIndex != null) {
                  setActiveStep(navigateToState.stepIndex);
                  isInternalNavigationRef.current = true;
                  navigate(navigateToState.link);
                }
                handleOnDiscardChanges(showSaveWarning?.newDocIds);
                setShowSaveWarning({
                  state: false,
                  newDocIds: null,
                });
              }}
              size="lg"
              isRounded={true}
              btnType="button"
              type="secondary-2"
              btnText={"Delete Changes"}
            />
            <MeritoButton
              size="lg"
              onClick={() => {
                // submitBankStatementData();
                setShowSaveWarning({
                  state: false,
                  newDocIds: null,
                });
              }}
              isRounded={true}
              btnType="button"
              type="primary-1"
              btnText="Continue Editing"
            />
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        modalStyles={{ maxWidth: "450px" }}
        showButton={false}
        visible={showBankStatementWarning?.state}
        toggle={() => {
          setShowBankStatementWarning({
            state: false,
            warningType: null,
            deleteDocIds: [],
          });
        }}
        showCloseBtn={false}
        size={"md"}
      >
        <div className={styles.confirmationContainer}>
          <div className={styles.topContainer}>
            <div className={styles.iconContainer}>
              <Exclamation />
            </div>
            <div className={styles.title}>
              {showBankStatementWarning?.warningType ==
                BankStatementWarningType.missingTransactions ? (
                <>Uploaded All Missing Statements?</>
              ) : (
                <>
                  Have You Uploaded Statements From All Your Accounts?
                  <br />
                  <br />
                </>
              )}
            </div>
            {showBankStatementWarning?.warningType ==
              BankStatementWarningType.missingTransactions ? (
              <div className={styles.subTitle}>
                Missing statements may mark the account <br /> as dormant for
                that period
                <br />
                <br />
                This may affect your credit eligibility.
              </div>
            ) : null}
          </div>

          <div className={styles.bottomContainer}>
            <MeritoButton
              onClick={() => {
                setShowBankStatementWarning({
                  state: false,
                  warningType: null,
                  deleteDocIds: [],
                });
              }}
              size="lg"
              isRounded={true}
              btnType="button"
              type="secondary-2"
              btnText={"Upload More"}
            />
            <MeritoButton
              size="lg"
              onClick={() => {
                submitBankStatementData();
              }}
              isRounded={true}
              btnType="button"
              type="primary-1"
              btnText="Confirm"
            />
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        modalStyles={{ maxWidth: "450px" }}
        showButton={false}
        visible={submitWarning?.state}
        toggle={() => {
          setSubmitWarning({
            state: false,
            formValues: null,
          });
        }}
        showCloseBtn={false}
        size={"md"}
      >
        <div className={styles.confirmationContainer}>
          <div className={styles.topContainer}>
            <div className={styles.iconContainer}>
              <Exclamation />
            </div>
            <div className={styles.title}>
              <>Required Details Are Missing!</>
            </div>
            <div className={styles.subTitle}>
              Do you want to proceed anyway?
            </div>
          </div>

          <div className={styles.bottomContainer}>
            <MeritoButton
              onClick={() => {
                // setShowBankStatementWarning({
                //     state: false,
                //     warningType: null,
                //     deleteDocIds: [],
                // })
                submitAfterSaveWarning(submitWarning?.formValues);
              }}
              size="lg"
              isRounded={true}
              btnType="button"
              type="secondary-2"
              btnText={"Save & Continue"}
            />
            <MeritoButton
              size="lg"
              onClick={() => {
                // submitBankStatementData();
                setSubmitWarning({
                  state: false,
                  formValues: null,
                });
              }}
              isRounded={true}
              btnType="button"
              type="primary-1"
              btnText="Continue Editing"
            />
          </div>
        </div>
      </ConfirmDialog>

      <UnderwritingDeclaration
        isSubmitLoading={submitLoading}
        handleClose={() => setIsDeclarationOpen(false)}
        isOpen={isDeclarationOpen}
        handleSubmit={(values) => handleFinalFormSubmit(values)}
      />

    </>
  );
};

export default UnderwritingSections;
